import { useEffect } from "react";
import { Check, Star, Crown, Zap, Coffee, Gamepad2 } from "lucide-react";

const membershipTiers = [
  {
    name: "Daily",
    price: "$5",
    period: "per day",
    description: "Perfect for occasional players",
    features: [
      "Full access for the day",
      "Cash game access",
      "Tournament eligibility",
      "Beverages available",
    ],
    popular: false,
  },
  {
    name: "Weekly",
    price: "$15",
    period: "per week",
    description: "Great for regular visitors",
    features: [
      "7-day full access",
      "Cash game access",
      "Tournament eligibility",
      "Beverages available",
      "Priority seating",
    ],
    popular: false,
  },
  {
    name: "Monthly",
    price: "$30",
    period: "per month",
    description: "Best value for frequent players",
    features: [
      "30-day full access",
      "Cash game access",
      "Tournament eligibility",
      "Beverages available",
      "Priority seating",
      "Member events access",
    ],
    popular: true,
  },
  {
    name: "Annual",
    price: "$175",
    period: "per year",
    description: "Ultimate poker commitment",
    features: [
      "365-day full access",
      "Cash game access",
      "Tournament eligibility",
      "Beverages available",
      "VIP priority seating",
      "Exclusive member events",
      "Save over $185/year",
    ],
    popular: false,
  },
];

export default function Membership() {
  // Scroll to top on page load
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen pt-20" data-testid="membership-page">
      {/* Hero Section */}
      <section className="py-24 md:py-32 bg-gradient-to-b from-casino-black to-casino-dark relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-20 left-1/4 w-96 h-96 bg-gold/20 rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-1/4 w-96 h-96 bg-casino-red/20 rounded-full blur-3xl" />
        </div>
        
        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center">
          <p className="text-gold uppercase tracking-[0.3em] text-sm mb-6 font-accent">
            Join the Club
          </p>
          <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl text-white mb-6">
            Membership <span className="gold-gradient">Plans</span>
          </h1>
          <p className="text-white/70 text-lg md:text-xl max-w-2xl mx-auto">
            Choose the membership that fits your poker lifestyle. All memberships include access to our premium poker room.
          </p>
        </div>
      </section>

      {/* Age Requirement Notice */}
      <section className="py-8 bg-casino-red/10 border-y border-casino-red/30">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-white font-medium">
            <span className="text-casino-red font-bold">Must be 18+</span> to become a member
            <span className="mx-4">•</span>
            One-time admin fee: <span className="text-gold font-bold">$20</span>
          </p>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-20 bg-casino-dark" data-testid="pricing-section">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {membershipTiers.map((tier, index) => (
              <div 
                key={tier.name}
                className={`relative rounded-2xl overflow-hidden ${
                  tier.popular 
                    ? 'bg-gradient-to-b from-gold/20 to-black/40 border-2 border-gold' 
                    : 'glass-card'
                }`}
                data-testid={`membership-card-${tier.name.toLowerCase()}`}
              >
                {tier.popular && (
                  <div className="absolute top-0 left-0 right-0 bg-gold text-black text-center py-2 text-xs uppercase tracking-widest font-bold">
                    Most Popular
                  </div>
                )}
                
                <div className={`p-8 ${tier.popular ? 'pt-14' : ''}`}>
                  <div className="mb-6">
                    <h3 className="font-heading text-2xl text-white mb-2">{tier.name}</h3>
                    <p className="text-white/50 text-sm">{tier.description}</p>
                  </div>
                  
                  <div className="mb-8">
                    <span className="font-heading text-5xl text-gold">{tier.price}</span>
                    <span className="text-white/50 ml-2">{tier.period}</span>
                  </div>
                  
                  <ul className="space-y-3 mb-8">
                    {tier.features.map((feature) => (
                      <li key={feature} className="flex items-center gap-3 text-white/70">
                        <Check className="w-5 h-5 text-gold shrink-0" />
                        <span className="text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <button 
                    className={`w-full font-bold uppercase tracking-wider py-4 text-sm transition-all duration-300 ${
                      tier.popular
                        ? 'bg-gold text-black hover:bg-gold-dark'
                        : 'border border-gold text-gold hover:bg-gold/10'
                    }`}
                    data-testid={`membership-cta-${tier.name.toLowerCase()}`}
                  >
                    Get Started
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Cash Table Fee */}
      <section className="py-20 bg-casino-black">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="glass-card p-12">
            <Crown className="w-16 h-16 text-gold mx-auto mb-6" />
            <h2 className="font-heading text-3xl md:text-4xl text-white mb-4">
              Cash Table Hourly Fee
            </h2>
            <div className="flex items-center justify-center gap-2 mb-6">
              <span className="font-heading text-6xl text-gold">$10</span>
              <span className="text-white/50">/hour</span>
            </div>
            <p className="text-white/60 max-w-xl mx-auto">
              In addition to your membership, cash game players pay a $10 hourly table fee. 
              This keeps our games running smoothly and ensures quality service.
            </p>
          </div>
        </div>
      </section>

      {/* Amenities */}
      <section className="py-20 bg-casino-dark">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <p className="text-gold uppercase tracking-widest text-sm mb-4">What's Included</p>
            <h2 className="font-heading text-3xl md:text-4xl text-white">Member Amenities</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="glass-card p-8 text-center" data-testid="amenity-beverages">
              <Coffee className="w-12 h-12 text-gold mx-auto mb-4" />
              <h3 className="font-heading text-xl text-white mb-2">Beverages & Snacks</h3>
              <p className="text-white/60">
                Keep refreshed during your session with our selection of beverages and snacks available for purchase.
              </p>
            </div>
            
            <div className="glass-card p-8 text-center" data-testid="amenity-machines">
              <Gamepad2 className="w-12 h-12 text-gold mx-auto mb-4" />
              <h3 className="font-heading text-xl text-white mb-2">Skill & Sweepstakes</h3>
              <p className="text-white/60">
                Take a break from the tables and enjoy our skill and sweepstakes machines.
              </p>
            </div>
            
            <div className="glass-card p-8 text-center" data-testid="amenity-community">
              <Star className="w-12 h-12 text-gold mx-auto mb-4" />
              <h3 className="font-heading text-xl text-white mb-2">Community Events</h3>
              <p className="text-white/60">
                Join our weekly tournaments, special events, and promotional giveaways.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-gradient-to-b from-casino-dark to-black">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <Zap className="w-16 h-16 text-gold mx-auto mb-8" />
          <h2 className="font-heading text-4xl md:text-5xl text-white mb-6">
            Ready to Play?
          </h2>
          <p className="text-white/70 text-lg mb-10 max-w-2xl mx-auto">
            Visit San Marcos Social Club today. Our friendly staff will help you get started 
            with the perfect membership for your poker journey.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <span
              className="bg-white/10 text-white/40 italic font-medium px-8 py-4 text-sm cursor-default"
              data-testid="cta-call"
            >
              Phone Coming Soon
            </span>
            <span
              className="border border-white/10 text-white/40 italic px-8 py-4 text-sm cursor-default"
              data-testid="cta-directions"
            >
              Directions Coming Soon
            </span>
          </div>
        </div>
      </section>
    </div>
  );
}
